package com.unrealmi.site.pages

import androidx.compose.runtime.Composable
import com.varabyte.kobweb.compose.css.AnimationIterationCount
import com.varabyte.kobweb.compose.css.Cursor
import com.varabyte.kobweb.compose.css.Overflow
import com.varabyte.kobweb.compose.css.TextAlign
import com.varabyte.kobweb.compose.css.TextDecorationLine
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.foundation.layout.Spacer
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.data.add
import com.varabyte.kobweb.core.init.InitRoute
import com.varabyte.kobweb.core.init.InitRouteContext
import com.varabyte.kobweb.core.layout.Layout
import com.varabyte.kobweb.silk.components.icons.fa.*
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.CssStyle
import com.varabyte.kobweb.silk.style.animation.toAnimation
import com.varabyte.kobweb.silk.style.base
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import com.varabyte.kobweb.silk.style.breakpoint.displayIfAtLeast
import com.varabyte.kobweb.silk.style.selectors.hover
import com.varabyte.kobweb.silk.style.toAttrs
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import com.unrealmi.site.*
import com.unrealmi.site.components.layouts.PageLayoutData

// ---------------------------------------------------------------------------------------------------------------------
// Shared section scaffolding
// ---------------------------------------------------------------------------------------------------------------------

/** Constrains a section's inner content to a comfortable max width and pads it responsively. */
val SectionStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(72.cssRem)
            .padding(leftRight = 1.5.cssRem, topBottom = 4.cssRem)
    }
    Breakpoint.MD { Modifier.padding(leftRight = 2.cssRem, topBottom = 6.cssRem) }
}

/** A frosted glass card that lifts and brightens its border on hover. */
val GlassCardStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .borderRadius(1.25.cssRem)
            .padding(1.75.cssRem)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .backgroundColor(colorMode.toSitePalette().surface)
            .styleModifier {
                property("backdrop-filter", "blur(12px)")
                property("-webkit-backdrop-filter", "blur(12px)")
            }
            .transition(
                Transition.of("transform", 220.ms),
                Transition.of("border-color", 220.ms),
                Transition.of("box-shadow", 220.ms),
            )
    }
    hover {
        Modifier
            .translateY((-6).px)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().borderStrong)
            .boxShadow(offsetY = 18.px, blurRadius = 40.px, color = colorMode.toSitePalette().glow)
    }
}

@Composable
private fun Eyebrow(text: String) {
    SpanText(text, EyebrowStyle.toModifier())
}

// ---------------------------------------------------------------------------------------------------------------------
// Hero
// ---------------------------------------------------------------------------------------------------------------------

val HeroSectionStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .maxWidth(72.cssRem)
            .padding(leftRight = 1.5.cssRem, top = 3.cssRem, bottom = 3.cssRem)
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { size(1.fr) }
            .gap(3.cssRem)
    }
    Breakpoint.MD {
        Modifier
            .padding(leftRight = 2.cssRem, top = 6.cssRem, bottom = 5.cssRem)
            .gridTemplateColumns { size(1.1.fr); size(0.9.fr) }
            .gap(4.cssRem)
    }
}

@Composable
private fun StatusPill() {
    val palette = ColorMode.current.toSitePalette()
    Row(
        Modifier
            .gap(0.5.cssRem)
            .padding(topBottom = 0.4.cssRem, leftRight = 0.85.cssRem)
            .borderRadius(2.cssRem)
            .border(1.px, LineStyle.Solid, palette.border)
            .backgroundColor(palette.surface),
        verticalAlignment = Alignment.CenterVertically,
    ) {
        Box(
            Modifier
                .size(0.5.cssRem)
                .borderRadius(50.percent)
                .backgroundColor(palette.brand.cyan)
                .animation(PulseAnim.toAnimation(duration = 1.8.s, iterationCount = AnimationIterationCount.Infinite))
        )
        SpanText(
            "AI · 让智能触手可及",
            Modifier.fontSize(0.8.cssRem).letterSpacing(0.5.px).color(palette.subtle)
        )
    }
}

@Composable
private fun PrimaryCta(text: String, href: String) {
    Link(
        href,
        Modifier
            .textDecorationLine(TextDecorationLine.None)
            .padding(topBottom = 0.85.cssRem, leftRight = 1.6.cssRem)
            .borderRadius(2.cssRem)
            .fontSize(1.cssRem)
            .fontWeight(600)
            .color(Colors.White)
            .cursor(Cursor.Pointer)
            .styleModifier {
                property("background", brandGradient(120))
                property("box-shadow", "0 10px 30px rgba(124,92,255,0.4)")
            },
    ) { SpanText(text) }
}

@Composable
private fun SecondaryCta(text: String, href: String) {
    val palette = ColorMode.current.toSitePalette()
    Link(
        href,
        Modifier
            .textDecorationLine(TextDecorationLine.None)
            .padding(topBottom = 0.85.cssRem, leftRight = 1.6.cssRem)
            .borderRadius(2.cssRem)
            .fontSize(1.cssRem)
            .fontWeight(600)
            .border(1.px, LineStyle.Solid, palette.border)
            .backgroundColor(palette.surface)
            .color(palette.brand.primary)
            .cursor(Cursor.Pointer),
    ) { SpanText(text) }
}

/** Decorative animated "AI core" visual: a glowing orb wrapped by a slowly rotating conic ring. */
@Composable
private fun HeroVisual() {
    Box(
        Modifier.fillMaxWidth().displayIfAtLeast(Breakpoint.MD),
        contentAlignment = Alignment.Center,
    ) {
        Box(Modifier.size(24.cssRem), contentAlignment = Alignment.Center) {
            // Rotating conic ring.
            Div(
                Modifier
                    .size(24.cssRem)
                    .borderRadius(50.percent)
                    .styleModifier {
                        property(
                            "background",
                            "conic-gradient(from 0deg, transparent 0%, rgba(124,92,255,0.0) 35%, #B568FE 70%, #38BDF8 85%, transparent 100%)"
                        )
                        property("-webkit-mask", "radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))")
                        property("mask", "radial-gradient(farthest-side, transparent calc(100% - 3px), black calc(100% - 2px))")
                    }
                    .animation(SpinAnim.toAnimation(duration = 12.s, iterationCount = AnimationIterationCount.Infinite, timingFunction = AnimationTimingFunction.Linear))
                    .toAttrs()
            )
            // Second, thinner ring rotating the other way.
            Div(
                Modifier
                    .size(18.cssRem)
                    .borderRadius(50.percent)
                    .styleModifier {
                        property(
                            "background",
                            "conic-gradient(from 180deg, transparent 0%, rgba(72,33,203,0.0) 40%, #7C5CFF 75%, transparent 100%)"
                        )
                        property("-webkit-mask", "radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 1px))")
                        property("mask", "radial-gradient(farthest-side, transparent calc(100% - 2px), black calc(100% - 1px))")
                    }
                    .animation(SpinAnim.toAnimation(duration = 18.s, iterationCount = AnimationIterationCount.Infinite, direction = AnimationDirection.Reverse, timingFunction = AnimationTimingFunction.Linear))
                    .toAttrs()
            )
            // Glowing core.
            Div(
                Modifier
                    .size(9.cssRem)
                    .borderRadius(50.percent)
                    .styleModifier {
                        property("background", "radial-gradient(circle at 35% 30%, #C89BFF, #7C5CFF 45%, #4821CB 100%)")
                        property("box-shadow", "0 0 60px rgba(124,92,255,0.7), inset 0 0 30px rgba(255,255,255,0.25)")
                    }
                    .toAttrs()
            )
            SpanText(
                "AI",
                Modifier.fontSize(2.2.cssRem).fontWeight(800).color(Colors.White)
                    .styleModifier { property("text-shadow", "0 2px 12px rgba(0,0,0,0.35)") }
            )
        }
    }
}

@Composable
private fun HeroSection() {
    val palette = ColorMode.current.toSitePalette()
    Div(
        HeroSectionStyle.toModifier()
            .animation(RiseAnim.toAnimation(duration = 700.ms, timingFunction = AnimationTimingFunction.EaseOut))
            .toAttrs()
    ) {
        Column(Modifier.gap(1.5.cssRem), horizontalAlignment = Alignment.Start) {
            StatusPill()

            Column(Modifier.gap(0.75.cssRem)) {
                Div(HeadlineTextStyle.toModifier().styleModifier { property("font-size", "clamp(2.4rem, 6vw, 3.6rem)") }.toAttrs()) {
                    SpanText("让 AI 应用", Modifier.display(DisplayStyle.Block))
                    SpanText("触手可及", Modifier.display(DisplayStyle.Block).gradientText())
                }
                SpanText(
                    "Make AI Simple, Make Life Better",
                    Modifier
                        .fontFamily("JetBrains Mono", "SFMono-Regular", "Menlo", "monospace")
                        .fontSize(0.95.cssRem)
                        .letterSpacing(1.px)
                        .color(palette.brand.primary)
                )
            }

            SpanText(
                "上海虚米网络科技有限公司专注于 AI 应用开发，用最简单的方式，让最先进的人工智能技术走进每一个日常场景。",
                SubheadlineTextStyle.toModifier().maxWidth(34.cssRem)
            )

            Row(Modifier.gap(1.cssRem).flexWrap(FlexWrap.Wrap).margin(top = 0.5.cssRem), verticalAlignment = Alignment.CenterVertically) {
                PrimaryCta("联系我们", "mailto:contact@unrealmi.com")
                SecondaryCta("了解更多", "/about")
            }
        }

        HeroVisual()
    }
}

// ---------------------------------------------------------------------------------------------------------------------
// Stats strip
// ---------------------------------------------------------------------------------------------------------------------

private class Stat(val value: String, val label: String)

private val STATS = listOf(
    Stat("2023", "创立于上海"),
    Stat("AI-First", "技术驱动"),
    Stat("扁平化", "精英团队"),
    Stat("跨平台", "广泛适用"),
)

// 2 columns on mobile, 4 on desktop.
val StatsGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { repeat(2) { size(1.fr) } }
            .gap(1.cssRem)
            .borderRadius(1.25.cssRem)
            .padding(1.5.cssRem)
            .border(1.px, LineStyle.Solid, colorMode.toSitePalette().border)
            .backgroundColor(colorMode.toSitePalette().surface)
            .styleModifier {
                property("backdrop-filter", "blur(12px)")
                property("-webkit-backdrop-filter", "blur(12px)")
            }
    }
    Breakpoint.MD { Modifier.gridTemplateColumns { repeat(4) { size(1.fr) } } }
}

@Composable
private fun StatsStrip() {
    val palette = ColorMode.current.toSitePalette()
    Div(
        SectionStyle.toModifier().padding(topBottom = 1.5.cssRem).toAttrs()
    ) {
        Div(StatsGridStyle.toAttrs()) {
            STATS.forEach { stat ->
                Column(
                    Modifier.fillMaxWidth().padding(topBottom = 0.75.cssRem),
                    horizontalAlignment = Alignment.CenterHorizontally
                ) {
                    SpanText(stat.value, Modifier.fontSize(1.8.cssRem).fontWeight(800).gradientText())
                    SpanText(stat.label, Modifier.fontSize(0.85.cssRem).color(palette.subtle).margin(top = 0.35.cssRem))
                }
            }
        }
    }
}

// ---------------------------------------------------------------------------------------------------------------------
// Capabilities
// ---------------------------------------------------------------------------------------------------------------------

private class Capability(
    val icon: @Composable () -> Unit,
    val title: String,
    val desc: String,
    val points: List<String>,
)

@Composable
private fun capabilities(): List<Capability> = listOf(
    Capability(
        icon = { FaRobot(Modifier.fontSize(1.5.cssRem)) },
        title = "AI 核心技术",
        desc = "运用先进的机器学习与深度学习，打造高效、可靠的 AI 解决方案。",
        points = listOf("智能算法优化", "自然语言处理", "个性化 AI 服务"),
    ),
    Capability(
        icon = { FaWandMagicSparkles(Modifier.fontSize(1.5.cssRem)) },
        title = "产品开发能力",
        desc = "敏捷开发流程，快速把 AI 创意打磨成直观易用的产品。",
        points = listOf("快速原型设计", "跨平台部署", "用户体验优化"),
    ),
    Capability(
        icon = { FaLightbulb(Modifier.fontSize(1.5.cssRem)) },
        title = "应用场景创新",
        desc = "让 AI 贴近真实生活，创造有价值、有温度的应用体验。",
        points = listOf("生活助手 AI", "工作效率提升", "学习与娱乐"),
    ),
)

val CapabilityGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { size(1.fr) }
            .gap(1.5.cssRem)
    }
    Breakpoint.MD {
        Modifier.gridTemplateColumns { repeat(3) { size(1.fr) } }
    }
}

@Composable
private fun CapabilityCard(cap: Capability) {
    val palette = ColorMode.current.toSitePalette()
    Column(GlassCardStyle.toModifier().gap(1.cssRem), horizontalAlignment = Alignment.Start) {
        Box(
            Modifier
                .size(3.cssRem)
                .borderRadius(0.85.cssRem)
                .color(Colors.White)
                .styleModifier {
                    property("background", brandGradient(135))
                    property("box-shadow", "0 8px 20px rgba(124,92,255,0.35)")
                },
            contentAlignment = Alignment.Center,
        ) { cap.icon() }

        SpanText(cap.title, Modifier.fontSize(1.3.cssRem).fontWeight(700))
        SpanText(cap.desc, Modifier.fontSize(0.95.cssRem).color(palette.subtle).lineHeight(1.6))

        Column(Modifier.gap(0.5.cssRem).margin(top = 0.5.cssRem)) {
            cap.points.forEach { point ->
                Row(Modifier.gap(0.6.cssRem), verticalAlignment = Alignment.CenterVertically) {
                    FaCircleCheck(Modifier.fontSize(0.9.cssRem).color(palette.brand.cyan))
                    SpanText(point, Modifier.fontSize(0.9.cssRem))
                }
            }
        }
    }
}

@Composable
private fun CapabilitiesSection() {
    Div(SectionStyle.toModifier().id("capabilities").toAttrs()) {
        Column(Modifier.fillMaxWidth().gap(0.75.cssRem).margin(bottom = 2.5.cssRem)) {
            Eyebrow("// CORE CAPABILITIES")
            SpanText("我们能做什么", SectionTitleStyle.toModifier())
            SpanText(
                "从底层算法到终端产品，UnrealMi 覆盖 AI 应用落地的完整链路。",
                SubheadlineTextStyle.toModifier().maxWidth(40.cssRem)
            )
        }

        Div(CapabilityGridStyle.toAttrs()) {
            capabilities().forEach { CapabilityCard(it) }
        }
    }
}

// ---------------------------------------------------------------------------------------------------------------------
// Vision & Mission
// ---------------------------------------------------------------------------------------------------------------------

val VisionGridStyle = CssStyle {
    base {
        Modifier
            .fillMaxWidth()
            .display(DisplayStyle.Grid)
            .gridTemplateColumns { size(1.fr) }
            .gap(1.5.cssRem)
    }
    Breakpoint.MD { Modifier.gridTemplateColumns { repeat(2) { size(1.fr) } } }
}

@Composable
private fun VisionCard(eyebrow: String, title: String, body: String, icon: @Composable () -> Unit) {
    val palette = ColorMode.current.toSitePalette()
    Column(GlassCardStyle.toModifier().gap(1.cssRem)) {
        Row(Modifier.gap(0.75.cssRem), verticalAlignment = Alignment.CenterVertically) {
            Box(
                Modifier.size(2.6.cssRem).borderRadius(0.75.cssRem).color(palette.brand.primary)
                    .backgroundColor(palette.surfaceStrong).border(1.px, LineStyle.Solid, palette.border),
                contentAlignment = Alignment.Center,
            ) { icon() }
            Eyebrow(eyebrow)
        }
        SpanText(title, Modifier.fontSize(1.4.cssRem).fontWeight(700))
        SpanText(body, Modifier.fontSize(0.98.cssRem).color(palette.subtle).lineHeight(1.7))
    }
}

@Composable
private fun VisionSection() {
    Div(SectionStyle.toModifier().id("vision").toAttrs()) {
        Column(Modifier.fillMaxWidth().gap(0.75.cssRem).margin(bottom = 2.5.cssRem)) {
            Eyebrow("// VISION & MISSION")
            SpanText("愿景与使命", SectionTitleStyle.toModifier())
        }
        Div(VisionGridStyle.toAttrs()) {
            VisionCard(
                "VISION 愿景",
                "让 AI 触手可及，让生活更加智能美好",
                "我们相信 AI 不该是高高在上的黑科技，而应是每个人都能轻松使用的生活工具。通过简化复杂的技术，让人工智能真正走进千家万户。",
                { FaEye(Modifier.fontSize(1.2.cssRem)) },
            )
            VisionCard(
                "MISSION 使命",
                "Make AI Simple, Make Life Better",
                "用最简单的方式，让最先进的 AI 技术服务于最普通的生活场景。持续创新与打磨，消除 AI 应用的技术门槛。",
                { FaRocket(Modifier.fontSize(1.2.cssRem)) },
            )
        }
    }
}

// ---------------------------------------------------------------------------------------------------------------------
// Call to action
// ---------------------------------------------------------------------------------------------------------------------

@Composable
private fun CtaSection() {
    val palette = ColorMode.current.toSitePalette()
    Div(SectionStyle.toModifier().toAttrs()) {
        Column(
            Modifier
                .fillMaxWidth()
                .borderRadius(1.75.cssRem)
                .padding(topBottom = 3.5.cssRem, leftRight = 2.cssRem)
                .textAlign(TextAlign.Center)
                .border(1.px, LineStyle.Solid, palette.border)
                .position(Position.Relative)
                .overflow(Overflow.Hidden)
                .styleModifier {
                    property(
                        "background",
                        "radial-gradient(ellipse at 50% 0%, rgba(124,92,255,0.22), transparent 70%), ${palette.surface.toRgb().let { "rgba(${it.red},${it.green},${it.blue},${it.alpha / 255f})" }}"
                    )
                    property("backdrop-filter", "blur(12px)")
                    property("-webkit-backdrop-filter", "blur(12px)")
                },
            horizontalAlignment = Alignment.CenterHorizontally,
        ) {
            Eyebrow("// JOIN US")
            SpanText(
                "一起改变世界",
                SectionTitleStyle.toModifier().margin(top = 0.75.cssRem, bottom = 0.75.cssRem)
            )
            SpanText(
                "如果你相信 AI 能让世界变得更美好，欢迎有志于 AI 技术发展的人才、合作伙伴与我们同行。",
                SubheadlineTextStyle.toModifier().maxWidth(38.cssRem).textAlign(TextAlign.Center).margin(bottom = 2.cssRem)
            )
            Row(
                Modifier.gap(1.cssRem).flexWrap(FlexWrap.Wrap),
                horizontalArrangement = Arrangement.Center
            ) {
                PrimaryCta("contact@unrealmi.com", "mailto:contact@unrealmi.com")
                SecondaryCta("关于我们", "/about")
            }
        }
    }
}

// ---------------------------------------------------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------------------------------------------------

@InitRoute
fun initHomePage(ctx: InitRouteContext) {
    ctx.data.add(PageLayoutData("Home"))
}

@Page
@Layout(".components.layouts.PageLayout")
@Composable
fun HomePage() {
    Column(Modifier.fillMaxWidth(), horizontalAlignment = Alignment.CenterHorizontally) {
        HeroSection()
        StatsStrip()
        CapabilitiesSection()
        VisionSection()
        CtaSection()
    }
}
